import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { listOrgMembers } from '../services/api';
import {
  listRogerDocAccountLinks,
  type RogerDocClinicianAccount,
} from '../services/accountLinkApi';
import { useStore } from '../store';

/**
 * - `unavailable`: the caller may not read the source lists, so there is
 *   nothing to wait for.
 * - `loading`: the lists are in flight; the lookup is empty but not yet
 *   meaningfully empty.
 * - `ready`: the lists loaded. An absent username genuinely has no phone.
 * - `error`: a list failed. The lookup is empty because we could not ask.
 *
 * Only `ready` says an empty lookup is the truth, so it is the only status a
 * phone column should be rendered or exported under.
 */
export type ClinicianPhoneLookupStatus =
  'unavailable' | 'loading' | 'ready' | 'error';

export interface ClinicianPhoneLookup {
  /** Lowercased RogerDoc username -> phone number on the linked RogerEMR user. */
  phonesByUsername: Map<string, string>;
  status: ClinicianPhoneLookupStatus;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | {
      phase: 'ready';
      /** The agency the two lists were loaded for. */
      agencyId: string;
      accounts: RogerDocClinicianAccount[];
      phonesByEmail: Map<string, string>;
    };

const EMPTY_LOOKUP: Map<string, string> = new Map();

/**
 * Resolves clinician phone numbers for the adoption reports, which identify a
 * clinician by RogerDoc username while the phone number lives on the RogerEMR
 * user. RogerDoc owns the association between the two, so the username has to
 * be resolved through the account link's RogerEMR email.
 *
 * Both source lists are gated on clinician-mgr:access, which the adoption report
 * itself does not require. Rather than fetch and fail, callers get
 * `status: 'unavailable'` and drop the phone column entirely, so a user who
 * cannot see phone numbers never gets a column of blanks. The same holds while
 * the lists are still loading or after they failed: an empty lookup only means
 * "no phone numbers" once the status is `ready`.
 */
export function useClinicianPhoneLookup(): ClinicianPhoneLookup {
  const { agencyId, hasPermission } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      hasPermission: state.hasPermission,
    }))
  );

  const mayFetch = hasPermission('clinician-mgr:access') && !!agencyId;

  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });

  useEffect(() => {
    if (!mayFetch || !agencyId) {
      return;
    }

    let cancelled = false;
    setLoad({ phase: 'loading' });

    const run = async () => {
      try {
        const [membersRes, linkedAccounts] = await Promise.all([
          listOrgMembers({}),
          listRogerDocAccountLinks(),
        ]);
        if (cancelled) return;

        const phonesByEmail = new Map<string, string>();
        for (const member of membersRes.data ?? []) {
          const phone = member.phone_number?.trim();
          if (phone) {
            phonesByEmail.set(member.email.toLowerCase(), phone);
          }
        }
        setLoad({
          phase: 'ready',
          agencyId,
          accounts: linkedAccounts,
          phonesByEmail,
        });
      } catch (err) {
        // Phone is supplementary here, so a failure leaves the report intact
        // without its phone column instead of surfacing an error.
        console.error('Failed to load clinician phone numbers:', err);
        if (!cancelled) {
          setLoad({ phase: 'error' });
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // agencyId is a dependency even though mayFetch already covers going from
    // no agency to one: switching between two agencies leaves the boolean
    // unchanged, and both requests resolve the agency from the auth context.
  }, [mayFetch, agencyId]);

  // Derived rather than stored so that losing access, or switching agencies,
  // stops reporting `ready` on the render it happens rather than one effect
  // later — the render in between would otherwise serve the old agency's
  // numbers under a status saying they are current.
  const status: ClinicianPhoneLookupStatus = !mayFetch
    ? 'unavailable'
    : load.phase === 'ready' && load.agencyId !== agencyId
      ? 'loading'
      : load.phase;

  const phonesByUsername = useMemo(() => {
    if (status !== 'ready' || load.phase !== 'ready') return EMPTY_LOOKUP;
    const byUsername = new Map<string, string>();
    for (const account of load.accounts) {
      const link = account.roger_emr;
      // Same agency scoping as buildRogerDocLinksByEmail: a link belonging to
      // another RogerEMR agency must not supply a phone number here.
      if (!link || link.roger_emr_agency_id !== load.agencyId) continue;
      const phone = load.phonesByEmail.get(link.user_email.toLowerCase());
      if (phone) {
        byUsername.set(account.username.toLowerCase(), phone);
      }
    }
    return byUsername;
  }, [load, status]);

  return { phonesByUsername, status };
}
